import clsx from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import { lazy, type ReactNode, Suspense, useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";

import BackToTop from "@/components/BackToTop";
import ZendeskWidget from "@/components/ZendeskWidget";
import {
  isRegistrationModalOpenAtom,
  registrationFormDataAtom,
} from "@/context/registration";

import Footer from "./footer";
import Header from "./header";

const RegistrationModal = lazy(() => import("@/components/RegistrationModal"));

interface LayoutProps {
  children: ReactNode;
  locationSearch: string;
}

export default function Layout({ children, locationSearch }: LayoutProps) {
  const isRegistrationModalOpen = useAtomValue(isRegistrationModalOpenAtom);
  const setIsRegistrationModalOpen = useSetAtom(isRegistrationModalOpenAtom);
  const setRegistrationFormData = useSetAtom(registrationFormDataAtom);

  // Once opened, keep the lazy modal mounted so its close transition can run.
  const [isRegistrationModalMounted, setIsRegistrationModalMounted] =
    useState(false);

  useEffect(() => {
    if (isRegistrationModalOpen) {
      setIsRegistrationModalMounted(true);
    }
  }, [isRegistrationModalOpen]);

  // Handle invite_code URL parameter.
  // When present, it becomes the (read-only) invitation code used for
  // registration and auto-opens the modal. Without it, the form keeps the
  // official fallback code.
  useEffect(() => {
    const inviteCode = new URLSearchParams(locationSearch).get("invite_code");
    if (inviteCode) {
      setRegistrationFormData((prev) => ({
        ...prev,
        invitationCode: inviteCode,
      }));
      setIsRegistrationModalOpen(true);
    }
  }, [locationSearch, setIsRegistrationModalOpen, setRegistrationFormData]);

  return (
    <div className={clsx("min-h-screen", "flex flex-col")}>
      <Header />
      <main className={clsx("flex-1", "w-full")}>{children}</main>
      <Footer />
      {isRegistrationModalMounted && (
        <Suspense fallback={null}>
          <RegistrationModal />
        </Suspense>
      )}
      <Toaster />
      <BackToTop />
      <ZendeskWidget />
    </div>
  );
}
